import { API_V1 } from '../config'
import { apiFetch } from './api'

export interface SignatureRequest {
  id: string
  koblink_id: string
  invited_email: string
  invitee_name: string
  status: 'waiting' | 'pending' | 'signed' | 'rejected' | 'expired' | 'cancelled'
  level: 'SES' | 'AES' | 'QES'
  signing_step: number | null
  signed_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  last_reminder_sent: string | null
  reminder_count: number
  expires_at: string | null
}

export interface SendReminderResponse {
  success: boolean
  message: string
  reminder_count: number
}

export interface EmailEvent {
  type: 'sent' | 'delivered' | 'delayed' | 'bounced' | 'complained' | 'opened' | 'clicked'
  occurred_at: string
}

export interface EmailStatus {
  signature_request_id: string
  email: string
  current_status: string
  has_problem: boolean
  events: EmailEvent[]
}

export interface EmailEventsResponse {
  document_id: string
  email_statuses: EmailStatus[]
}

/** Send a reminder to a signer who hasn't signed yet */
export async function sendReminder(
  signatureRequestId: string,
  actorId: string,
): Promise<SendReminderResponse> {
  const response = await apiFetch(
    `${API_V1}/signatures/reminder/${encodeURIComponent(signatureRequestId)}/`,
    {
      method: 'POST',
      body: JSON.stringify({ actor_id: actorId }),
    },
  )

  if (response.status === 429) {
    const data = await response.json().catch(() => null)
    throw new Error(data?.message || 'Reminder already sent recently. Please wait 24 hours.')
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new Error(data?.error || data?.message || `Failed to send reminder: ${response.status}`)
  }
  return response.json()
}

/** Get email delivery events for a document */
export async function getEmailEvents(koblinkId: string, actorId?: string): Promise<EmailEventsResponse> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/email-events/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/email-events/`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch email events: ${response.status}`)
  }
  return response.json()
}
